import React, { useRef, useEffect, useState } from 'react';
import { gsap, useReducedMotion } from '../../animations';
import { hexToRgb } from '../../lib/camera';
import { BotState, BotVariant, SHAPES, TEMPO, POSE, TIMING, HEAD_SHAPES, EYE_SHAPES } from './motionSystem';

export type { BotState, BotVariant };

/** Choose the stronger eye contrast for palette hex and corridor RGB fills. */
export function botEyeInk(color: string): string {
    const dark = '#0d0f18';
    const light = '#f7faf3';
    const hex = color.trim().match(/^#([\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i)?.[1];
    const rgb = color.trim().match(/^rgba?\(([^)]+)\)$/i)?.[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3);
    const channels = hex
        ? hexToRgb(`#${hex.length <= 4 ? hex.slice(0, 3).split('').map(c => c + c).join('') : hex.slice(0, 6)}`)
        : rgb?.map(c => parseFloat(c) * (c.endsWith('%') ? 2.55 : 1));
    if (!channels || channels.length !== 3 || channels.some(c => !Number.isFinite(c))) return dark;
    const luminance = (values: number[]) => values.map(c => {
        const normalized = Math.max(0, Math.min(255, c)) / 255;
        return normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
    }).reduce((sum, c, i) => sum + c * [0.2126, 0.7152, 0.0722][i], 0);
    const fill = luminance(channels);
    const contrast = (ink: string) => {
        const eye = luminance(hexToRgb(ink));
        return (Math.max(fill, eye) + 0.05) / (Math.min(fill, eye) + 0.05);
    };
    return contrast(light) > contrast(dark) ? light : dark;
}

interface BlobBotProps {
    color: string;
    state?: BotState;
    size?: number;
    trackPointer?: boolean;
    presence?: boolean;
    paused?: boolean;
    variant?: BotVariant;
    label?: string;
    onClick?: () => void;
    style?: React.CSSProperties;
}

/** One SVG character; each animation channel owns a separate transform layer. */
const BlobBot: React.FC<BlobBotProps> = ({
    color, state = 'idle', size = 96, trackPointer = false, presence = false, paused = false,
    variant = 'blob', label, onClick, style,
}) => {
    const eyeInk = botEyeInk(color);
    const svgRef = useRef<SVGSVGElement>(null);
    const poseRef = useRef<SVGGElement>(null);
    const driftRef = useRef<SVGGElement>(null);
    const acknowledgeRef = useRef<SVGGElement>(null);
    const headRef = useRef<SVGPathElement>(null);
    const gazeRef = useRef<SVGGElement>(null);
    const scanRef = useRef<SVGGElement>(null);
    const blinkRef = useRef<SVGGElement>(null);
    const eyePoseRefs = useRef<(SVGGElement | null)[]>([]);
    const eyePathRefs = useRef<(SVGPathElement | null)[]>([]);
    const satellitePoseRefs = useRef<(SVGGElement | null)[]>([]);
    const satelliteDriftRefs = useRef<(SVGGElement | null)[]>([]);
    const orbitAngle = useRef({ value: 0 });
    const previousState = useRef(state);
    const reduced = useReducedMotion();
    const [visible, setVisible] = useState(() => !document.hidden);

    useEffect(() => {
        // Establish origins once. Reapplying svgOrigin during an interrupted
        // tween would reinterpret it through that tween's current matrix.
        gsap.set([poseRef.current, driftRef.current, acknowledgeRef.current, blinkRef.current, ...satellitePoseRefs.current], {
            svgOrigin: '50 50',
        });
        gsap.set(eyePoseRefs.current, { svgOrigin: '0 0' });
    }, []);

    // Stop decorative work when the character cannot be seen. Visibility does
    // not reset the pose: returning resumes from the current shape and angle.
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        let inView = true;
        const sync = () => setVisible(inView && !document.hidden);
        const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
            inView = entries[0]?.isIntersecting ?? true;
            sync();
        });
        observer?.observe(svg);
        document.addEventListener('visibilitychange', sync);
        sync();
        return () => {
            observer?.disconnect();
            document.removeEventListener('visibilitychange', sync);
        };
    }, []);

    // Never revert on a state change: interruption must start at the live pose.
    useEffect(() => {
        const p = POSE[state];
        const timing = TIMING[state];
        const duration = reduced ? 0 : timing.duration;
        const transition = gsap.timeline({ defaults: { duration, ease: timing.ease, overwrite: 'auto' } });
        transition.to(poseRef.current, {
            x: p.x, y: p.y, rotation: p.rotation,
            scaleX: p.head * p.sx, scaleY: p.head * p.sy,
        }, 0);
        eyePoseRefs.current.forEach((eye, i) => {
            if (!eye) return;
            transition.to(eye, {
                x: p.eye.dx * 100,
                y: (i === 0 ? p.eye.lDy : p.eye.rDy) * 100,
                scaleX: p.eye.sx, scaleY: p.eye.sy,
                rotation: state === 'blocked' && i === 1 ? -p.eye.tilt : p.eye.tilt,
            }, 0);
        });
        eyePathRefs.current.forEach(eye => {
            if (eye) transition.to(eye, { attr: { d: state === 'done' ? EYE_SHAPES.smile : EYE_SHAPES.open } }, 0);
        });
        satellitePoseRefs.current.forEach((satellite, i) => {
            if (!satellite) return;
            const sat = p.sats[i];
            transition.to(satellite, {
                x: sat?.[0] ?? 0, opacity: sat?.[1] ?? 0,
                scale: sat ? 1 : 0.2,
            }, 0);
        });
        return () => { transition.kill(); };
    }, [state, reduced]);

    // Geometry changes are independent of expression and motion. Geometric
    // heads keep their silhouette; only the organic head changes its contour.
    useEffect(() => {
        headRef.current?.setAttribute('d', HEAD_SHAPES[variant]);
    }, [variant, reduced]);

    useEffect(() => {
        const drift = driftRef.current;
        const acknowledge = acknowledgeRef.current;
        const blink = blinkRef.current;
        const scan = scanRef.current;
        const head = headRef.current;
        const satellites = satelliteDriftRefs.current.filter((node): node is SVGGElement => !!node);
        if (!drift || !acknowledge || !blink || !scan || !head) return;
        const was = previousState.current;
        previousState.current = state;
        const targets = [drift, acknowledge, blink, scan, ...satellites];
        const timelines: gsap.core.Timeline[] = [];
        const stop = () => {
            timelines.forEach(timeline => timeline.kill());
            gsap.killTweensOf(orbitAngle.current);
            gsap.killTweensOf([head, ...targets]);
        };
        if (reduced || paused) {
            gsap.set(targets, { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1 });
            return stop;
        }
        if (!visible) return stop;

        const tempo = TEMPO[state];
        const breathing = gsap.timeline();
        timelines.push(breathing);
        breathing.to(drift, { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1, duration: 0.18 });
        if (tempo.breath) {
            const rest = state === 'waiting'
                ? { x: -0.8, y: 1.5, rotation: -1.3, scaleX: 1.01, scaleY: 0.99 }
                : state === 'working'
                    ? { x: 0.5, y: -1.2, rotation: -1, scaleX: 1.035, scaleY: 0.97 }
                    : { x: 0, y: -1.6, rotation: state === 'curious' ? 1.4 : 0, scaleX: 1.018, scaleY: 0.985 };
            breathing.to(drift, { ...rest, duration: tempo.breath, ease: 'sine.inOut', repeat: -1, yoyo: true });
        }

        // A blink closes and opens immediately; the pause belongs after opening.
        const blinking = gsap.timeline();
        timelines.push(blinking);
        blinking.to(blink, { scaleY: 1, duration: 0.1 });
        const blinkLoop = gsap.timeline({ repeat: -1 });
        blinkLoop.to(blink, { scaleY: 0.08, duration: 0.065, delay: tempo.blink, ease: 'power2.in' })
            .to(blink, { scaleY: 1, duration: 0.11, ease: 'power2.out' });
        blinking.add(blinkLoop);

        const scanning = gsap.timeline();
        timelines.push(scanning);
        scanning.to(scan, { x: 0, duration: 0.18 });
        if (state === 'thinking') {
            scanning.to(scan, { x: 3.5, duration: 0.8, ease: 'sine.inOut', repeat: -1, yoyo: true });
            const angle = orbitAngle.current;
            const orbit = gsap.timeline();
            timelines.push(orbit);
            satellites.forEach((satellite, i) => {
                const radius = i === 0 ? -38 : 38;
                orbit.to(satellite, {
                    x: Math.cos(angle.value) * radius - radius,
                    y: Math.sin(angle.value) * radius * 0.85,
                    duration: 0.3, ease: 'power2.out',
                }, 0);
            });
            orbit.to(angle, {
                value: angle.value + Math.PI * 2, duration: 3, ease: 'none', repeat: -1,
                onUpdate: () => satellites.forEach((satellite, i) => {
                    const radius = i === 0 ? -38 : 38;
                    gsap.set(satellite, {
                        x: Math.cos(angle.value) * radius - radius,
                        y: Math.sin(angle.value) * radius * 0.85,
                    });
                }),
            }, 0.3);
        } else {
            const settle = gsap.timeline();
            timelines.push(settle);
            settle.to(satellites, { x: 0, y: 0, duration: 0.2, ease: 'power2.out' });
        }

        if (variant === 'blob' && tempo.morph) {
            const contour = gsap.timeline();
            timelines.push(contour);
            // Settle the live outline once; repeated cycles share endpoints.
            contour.to(head, { attr: { d: SHAPES[0] }, duration: 0.2, ease: 'sine.inOut' });
            const contourLoop = gsap.timeline({ repeat: -1 });
            SHAPES.forEach((_, index) => {
                contourLoop.to(head, { attr: { d: SHAPES[(index + 1) % SHAPES.length] }, duration: tempo.morph, ease: 'sine.inOut' });
            });
            contour.add(contourLoop);
        }

        const beat = gsap.timeline();
        timelines.push(beat);
        beat.to(acknowledge, { x: 0, y: 0, scaleX: 1, scaleY: 1, duration: 0.12 });
        if (state !== was) {
            if (state === 'done') {
                beat.to(acknowledge, { y: -3, scaleX: 0.97, scaleY: 1.035, duration: 0.16, ease: 'power2.out' })
                    .to(acknowledge, { y: 0, scaleX: 1, scaleY: 1, duration: 0.3, ease: 'back.out(1.5)' });
            } else if (state === 'curious' || ((state === 'thinking' || state === 'working') && (was === 'idle' || was === 'curious' || was === 'done'))) {
                beat.to(acknowledge, { y: 1.8, scaleX: 1.035, scaleY: 0.965, duration: 0.12, ease: 'power2.out' })
                    .to(acknowledge, { y: 0, scaleX: 1, scaleY: 1, duration: 0.18, ease: 'power2.out' });
            } else if (state === 'blocked') {
                beat.to(acknowledge, { x: 0.8, duration: 0.06 })
                    .to(acknowledge, { x: -0.8, duration: 0.07 })
                    .to(acknowledge, { x: 0, duration: 0.09 });
            }
        }
        return stop;
    }, [state, variant, reduced, visible, paused]);

    useEffect(() => {
        const gaze = gazeRef.current;
        if (!gaze) return;
        if (!trackPointer || reduced || paused || !visible || !['idle', 'curious', 'done'].includes(state)) {
            gsap.to(gaze, { x: 0, y: 0, duration: reduced ? 0 : 0.16, overwrite: true });
            return () => { gsap.killTweensOf(gaze); };
        }
        const xTo = gsap.quickTo(gaze, 'x', { duration: 0.35, ease: 'power2.out' });
        const yTo = gsap.quickTo(gaze, 'y', { duration: 0.35, ease: 'power2.out' });
        const follow = (event: PointerEvent) => {
            if (event.pointerType === 'touch') return;
            const rect = svgRef.current?.getBoundingClientRect();
            if (!rect) return;
            const dx = event.clientX - rect.left - rect.width / 2;
            const dy = event.clientY - rect.top - rect.height / 2;
            const distance = Math.hypot(dx, dy) || 1;
            const reach = 7.5 * Math.min(1, distance / 420);
            xTo(dx / distance * reach);
            yTo(dy / distance * reach * 0.7);
        };
        window.addEventListener('pointermove', follow, { passive: true });
        return () => {
            window.removeEventListener('pointermove', follow);
            xTo.tween.kill();
            yTo.tween.kill();
        };
    }, [trackPointer, reduced, visible, state, paused]);

    const content = <>
        <svg
            ref={svgRef} width={size} height={size} viewBox="0 0 100 100"
            role={label && !onClick ? 'img' : undefined}
            aria-label={label && !onClick ? `${label}, ${state}` : undefined}
            aria-hidden={!label || !!onClick ? true : undefined}
            data-bot-state={state} data-bot-variant={variant}
            data-bot-motion={reduced ? 'reduced' : visible && !paused ? 'active' : 'paused'}
            style={{ display: 'block', overflow: 'visible', flexShrink: 0 }}
        >
            {([0, 1] as const).map(i => <g key={i} ref={node => { satellitePoseRefs.current[i] = node; }} data-bot-layer="satellite-pose" opacity="0">
                <g ref={node => { satelliteDriftRefs.current[i] = node; }}>
                    <g transform="translate(41 41) scale(.18)">
                        <path d={HEAD_SHAPES[variant]} fill={color} />
                        <path d={EYE_SHAPES.open} fill={eyeInk} transform="translate(38 50)" />
                        <path d={EYE_SHAPES.open} fill={eyeInk} transform="translate(62 50)" />
                    </g>
                </g>
            </g>)}
            <g ref={poseRef} data-bot-layer="pose">
                <g ref={driftRef} data-bot-layer="drift">
                    <g ref={acknowledgeRef} data-bot-layer="acknowledge">
                        <path ref={headRef} d={HEAD_SHAPES[variant]} fill={color} />
                        <g ref={gazeRef} data-bot-layer="gaze">
                            <g ref={scanRef} data-bot-layer="scan">
                                <g ref={blinkRef} data-bot-layer="blink">
                                    {([0, 1] as const).map(i => <g key={i} transform={`translate(${i === 0 ? 38 : 62} 50)`}>
                                        <g ref={node => { eyePoseRefs.current[i] = node; }} data-bot-layer="eye-pose">
                                            <path ref={node => { eyePathRefs.current[i] = node; }} d={EYE_SHAPES.open} fill={eyeInk} />
                                        </g>
                                    </g>)}
                                </g>
                            </g>
                        </g>
                    </g>
                </g>
            </g>
            {presence && <circle cx="91" cy="89" r="6.5" fill="#50fa7b" stroke="#0d0f18" strokeWidth="2" />}
        </svg>
        {label && <span aria-hidden style={{
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: '12px', color: '#a9b4cb',
        }}>{label}</span>}
    </>;
    const wrapperStyle: React.CSSProperties = {
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: label ? '10px' : 0,
        ...style,
    };
    return onClick
        ? <button type="button" onClick={onClick} aria-label={`${label || 'Agent'}, ${state}`} style={{
            border: 0, padding: 0, background: 'transparent', cursor: 'pointer', ...wrapperStyle,
        }}>{content}</button>
        : <span style={wrapperStyle}>{content}</span>;
};

export default BlobBot;
